import React, { useEffect } from 'react';
import { ModernNavigation } from '../components/modern/ModernNavigation';
import { ModernFooter } from '../components/modern/ModernFooter';
import '../css/modern-design-system.css';
import '../css/modern-how-it-works.css';

function ModernHowItWorks() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="modern-app">
      <ModernNavigation />

      {/* Hero Section */}
      <section className="page-hero">
        <div className="hero-background">
          <div className="gradient-orb gradient-orb-1"></div>
          <div className="gradient-orb gradient-orb-2"></div>
          <div className="grid-pattern"></div>
        </div>
        
        <div className="container">
          <div className="page-hero-content">
            <span className="section-label">SIMPLE PROCESS</span>
            <h1 className="page-title">
              How <span className="text-gradient">EquityWorkx</span> Works
            </h1>
            <p className="page-subtitle">
              Transform your mortgage into a wealth-building machine with our proven 
              3-step process. Join thousands who are already on their path to financial freedom.
            </p>
          </div>
        </div>
      </section>

      {/* Main Process Steps */}
      <section className="process-main">
        <div className="container">
          <div className="process-timeline">
            <div className="timeline-line"></div>
            
            <div className="process-step">
              <div className="step-marker">
                <span className="step-number">1</span>
              </div>
              <div className="step-content">
                <div className="step-text">
                  <h2 className="step-title">Connect Your Financial Accounts</h2>
                  <p className="step-description">
                    Start by securely connecting your mortgage and bank accounts. Our bank-level 
                    encryption ensures your data is always protected. We use read-only access, 
                    so your accounts remain completely secure.
                  </p>
                  <div className="step-features">
                    <div className="step-feature">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      <span>256-bit bank-level encryption</span>
                    </div>
                    <div className="step-feature">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      <span>Read-only access for security</span>
                    </div>
                    <div className="step-feature">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      <span>15,000+ institutions supported</span>
                    </div>
                  </div>
                </div>
                <div className="step-visual">
                  <div className="connection-visual">
                    <div className="bank-icon">🏦</div>
                    <div className="connection-line">
                      <div className="pulse"></div>
                    </div>
                    <div className="app-icon">📱</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="process-step">
              <div className="step-marker">
                <span className="step-number">2</span>
              </div>
              <div className="step-content">
                <div className="step-visual">
                  <div className="analysis-visual">
                    <div className="data-point" style={{animationDelay: '0s'}}>
                      <span className="data-label">Income</span>
                      <span className="data-value">$12,500</span>
                    </div>
                    <div className="data-point" style={{animationDelay: '0.2s'}}>
                      <span className="data-label">Mortgage</span>
                      <span className="data-value">$3,200</span>
                    </div>
                    <div className="data-point" style={{animationDelay: '0.4s'}}>
                      <span className="data-label">Expenses</span>
                      <span className="data-value">$4,800</span>
                    </div>
                    <div className="data-point result" style={{animationDelay: '0.6s'}}>
                      <span className="data-label">Optimization</span>
                      <span className="data-value">+$2,400/mo</span>
                    </div>
                  </div>
                </div>
                <div className="step-text">
                  <h2 className="step-title">Get Your Personalized Wealth Plan</h2>
                  <p className="step-description">
                    Our advanced analytics engine analyzes your financial situation and creates a customized wealth-building 
                    strategy. We identify opportunities to optimize your mortgage payments, reduce 
                    interest, and accelerate equity growth.
                  </p>
                  <div className="step-features">
                    <div className="step-feature">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      <span>Advanced financial analysis</span>
                    </div>
                    <div className="step-feature">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      <span>Custom optimization strategies</span>
                    </div>
                    <div className="step-feature">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      <span>Wealth projection modeling</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="process-step">
              <div className="step-marker">
                <span className="step-number">3</span>
              </div>
              <div className="step-content">
                <div className="step-text">
                  <h2 className="step-title">Track Progress & Optimize</h2>
                  <p className="step-description">
                    Monitor your wealth-building journey with real-time dashboards and insights. 
                    Get alerts when opportunities arise and adjust your strategy as your situation 
                    changes. Our system continuously optimizes your plan for maximum results.
                  </p>
                  <div className="step-features">
                    <div className="step-feature">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      <span>Real-time progress tracking</span>
                    </div>
                    <div className="step-feature">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      <span>Smart opportunity alerts</span>
                    </div>
                    <div className="step-feature">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      <span>Continuous optimization</span>
                    </div>
                  </div>
                </div>
                <div className="step-visual">
                  <div className="dashboard-mockup">
                    <div className="dashboard-header">Dashboard</div>
                    <div className="dashboard-stats">
                      <div className="stat-card">
                        <span className="stat-icon">📈</span>
                        <span className="stat-value">+34.2%</span>
                        <span className="stat-label">Growth</span>
                      </div>
                      <div className="stat-card">
                        <span className="stat-icon">💰</span>
                        <span className="stat-value">$487K</span>
                        <span className="stat-label">Equity</span>
                      </div>
                      <div className="stat-card">
                        <span className="stat-icon">⏱️</span>
                        <span className="stat-value">12 Yrs</span>
                        <span className="stat-label">Saved</span>
                      </div>
                    </div>
                    <div className="dashboard-chart">
                      <div className="chart-line"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Strategy Section */}
      <section className="strategy-section">
        <div className="container">
          <div className="section-header-modern text-center">
            <span className="section-label">THE MORTGAGE MILLIONAIRE STRATEGY</span>
            <h2 className="section-title-modern">
              The Science Behind <span className="text-gradient">Building Wealth</span>
            </h2>
            <p className="section-subtitle-modern">
              Our proven strategy combines mortgage optimization with smart investing to 
              help you build wealth faster than traditional methods.
            </p>
          </div>

          <div className="strategy-grid">
            <div className="strategy-card">
              <div className="strategy-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7V12C2 16.5 4.23 20.68 12 22C19.77 20.68 22 16.5 22 12V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 8V8.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Reduce Interest Payments</h3>
              <p>
                Strategic payment timing and extra principal payments can save you 
                hundreds of thousands in interest over the life of your loan.
              </p>
              <div className="strategy-stat">
                <span className="stat-number">$284,000</span>
                <span className="stat-context">Average interest saved</span>
              </div>
            </div>

            <div className="strategy-card">
              <div className="strategy-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M20.59 13.41L13.42 20.58C13.2343 20.766 13.0137 20.9135 12.7709 21.0141C12.5281 21.1148 12.2678 21.1666 12.005 21.1666C11.7422 21.1666 11.4819 21.1148 11.2391 21.0141C10.9963 20.9135 10.7757 20.766 10.59 20.58L2 12V2H12L20.59 10.59C20.9625 10.9647 21.1716 11.4716 21.1716 12C21.1716 12.5284 20.9625 13.0353 20.59 13.41Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 7H7.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Build Equity Faster</h3>
              <p>
                Accelerate your equity growth by optimizing payment schedules and 
                leveraging our proprietary wealth-building algorithms.
              </p>
              <div className="strategy-stat">
                <span className="stat-number">3.5x</span>
                <span className="stat-context">Faster equity growth</span>
              </div>
            </div>

            <div className="strategy-card">
              <div className="strategy-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2V6M12 6L9 3M12 6L15 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Invest the Difference</h3>
              <p>
                Redirect saved interest into high-yield investments to compound 
                your wealth and reach millionaire status years sooner.
              </p>
              <div className="strategy-stat">
                <span className="stat-number">12 Years</span>
                <span className="stat-context">Sooner to $1M</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Visualization */}
      <section className="timeline-section">
        <div className="container">
          <h2 className="section-title-modern text-center">
            Your Journey to <span className="text-gradient">Financial Freedom</span>
          </h2>
          
          <div className="journey-timeline">
            <div className="timeline-track"></div>
            
            <div className="timeline-milestone">
              <div className="milestone-marker">📍</div>
              <div className="milestone-content">
                <h4>Today</h4>
                <p>Start your journey</p>
                <span className="milestone-value">$0</span>
              </div>
            </div>

            <div className="timeline-milestone">
              <div className="milestone-marker">🏠</div>
              <div className="milestone-content">
                <h4>Year 5</h4>
                <p>Build significant equity</p>
                <span className="milestone-value">$250K</span>
              </div>
            </div>

            <div className="timeline-milestone">
              <div className="milestone-marker">💎</div>
              <div className="milestone-content">
                <h4>Year 10</h4>
                <p>Mortgage-free</p>
                <span className="milestone-value">$650K</span>
              </div>
            </div>

            <div className="timeline-milestone">
              <div className="milestone-marker">🎯</div>
              <div className="milestone-content">
                <h4>Year 15</h4>
                <p>Millionaire status</p>
                <span className="milestone-value">$1.2M</span>
              </div>
            </div>

            <div className="timeline-milestone">
              <div className="milestone-marker">🚀</div>
              <div className="milestone-content">
                <h4>Year 20</h4>
                <p>Generational wealth</p>
                <span className="milestone-value">$2.4M</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header-modern text-center">
            <span className="section-label">FREQUENTLY ASKED QUESTIONS</span>
            <h2 className="section-title-modern">
              Got <span className="text-gradient">Questions?</span> We Have Answers
            </h2>
          </div>

          <div className="faq-grid">
            <div className="faq-item">
              <h3 className="faq-question">How secure is my financial data?</h3>
              <p className="faq-answer">
                We use bank-level 256-bit encryption to protect your data. 
                Your data is read-only, meaning we can never move money or make changes to your accounts.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">How long does it take to see results?</h3>
              <p className="faq-answer">
                Most users see immediate insights and can start optimizing within days. 
                Significant wealth building typically becomes visible within 3-6 months.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">What if I don't have a mortgage yet?</h3>
              <p className="faq-answer">
                EquityWorkx helps prospective homeowners plan their purchase strategy and 
                shows how to structure their mortgage for maximum wealth building from day one.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">Can I cancel anytime?</h3>
              <p className="faq-answer">
                Yes! We offer flexible month-to-month subscriptions with no long-term contracts. 
                Cancel anytime directly from your account settings.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">Do you provide financial advice?</h3>
              <p className="faq-answer">
                EquityWorkx provides educational content and data-driven insights. For personalized 
                advice, we connect you with certified financial advisors in our network.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">How is this different from refinancing?</h3>
              <p className="faq-answer">
                Our strategy optimizes your existing mortgage without the costs and hassle of refinancing. 
                We also help you identify when refinancing would be beneficial.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-modern">
        <div className="container">
          <div className="cta-content-modern">
            <h2 className="cta-title-modern">
              Ready to Start Your <span className="text-gradient">Wealth Journey?</span>
            </h2>
            <p className="cta-subtitle-modern">
              Join thousands of homeowners who are building generational wealth with EquityWorkx.
              Start with our free wealth assessment today.
            </p>
            <div className="cta-buttons-modern">
              <a href="/download" className="btn btn-primary btn-lg">
                Get Started Free
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7 10H13M13 10L10 7M13 10L10 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a href="/features" className="btn btn-secondary btn-lg">
                Explore Features
              </a>
            </div>
            <div className="cta-features">
              <div className="cta-feature">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>No credit card required</span>
              </div>
              <div className="cta-feature">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>Free wealth assessment</span>
              </div>
              <div className="cta-feature">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ModernFooter />
    </div>
  );
}

export default ModernHowItWorks;
